// src/components/Profile/ImageUploadModal.tsx
// Two-step profile image upload: Step 1 = select & validate, Step 2 = crop & save

import React, { useRef, useState, useEffect, useCallback } from 'react';
import './ImageUploadModal.css';

interface ImageUploadModalProps {
  onClose: () => void;
  onSave: (blob: Blob, filename: string) => Promise<void>;
}

type Step = 1 | 2;

interface CropRect {
  x: number;
  y: number;
  size: number;
}

const MIN_CROP = 60;

export const ImageUploadModal: React.FC<ImageUploadModalProps> = ({ onClose, onSave }) =>
{
  /* ── Step 1 state ──────────────────────────────── */
  const [step, setStep] = useState<Step>(1);
  const [fileName, setFileName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ── Step 2 state ──────────────────────────────── */
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewRef = useRef<HTMLCanvasElement>(null);
  const [imageBitmap, setImageBitmap] = useState<ImageBitmap | null>(null);
  const [crop, setCrop] = useState<CropRect>({ x: 0, y: 0, size: 200 });
  const dragging = useRef(false);
  const resizing = useRef<'TL' | 'TR' | 'BL' | 'BR' | null>(null);
  const dragStart = useRef({ mx: 0, my: 0, cx: 0, cy: 0 });
  const [saving, setSaving] = useState(false);

  /* ── Validation (mirrors legacy AngularJS logic) ─ */
  const validate = (file: File): string =>
  {
    if (/\.(jfif|gif)$/i.test(file.name))
    {
      return 'Invalid format. Only JPG, PNG or JPEG images are accepted.';
    }
    if (file.size >= 1_048_576)
    {
      return 'The image must be less than 1 MB (1048.576 KB).';
    }
    if (!/\.(jpg|png|jpeg)$/i.test(file.name))
    {
      return 'Invalid format. Only JPG, PNG or JPEG images are accepted.';
    }
    return '';
  };

  const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    const file = e.target.files?.[0];
    if (!file) return;

    const err = validate(file);
    if (err)
    {
      setErrorMsg(err);
      setFileName('');
      setSelectedFile(null);
      e.target.value = '';
      return;
    }

    setErrorMsg('');
    setFileName(file.name);
    setSelectedFile(file);
    e.target.value = '';
  };

  /* ── Step 2: Load image into canvas ─────────────── */
  useEffect(() =>
  {
    if (step !== 2 || !selectedFile) return;

    createImageBitmap(selectedFile).then(bmp =>
    {
      setImageBitmap(bmp);
    });
  }, [step, selectedFile]);

  /* ── Draw main canvas + crop overlay ────────────── */
  const drawCanvas = useCallback(() =>
  {
    const canvas = canvasRef.current;
    const bmp = imageBitmap;
    if (!canvas || !bmp) return;

    const ctx = canvas.getContext('2d')!;
    const cw = canvas.width;
    const ch = canvas.height;

    // Scale image to fit canvas
    const scale = Math.min(cw / bmp.width, ch / bmp.height);
    const iw = bmp.width * scale;
    const ih = bmp.height * scale;
    const ox = (cw - iw) / 2;
    const oy = (ch - ih) / 2;

    ctx.clearRect(0, 0, cw, ch);
    ctx.drawImage(bmp, ox, oy, iw, ih);

    // Darken outside crop
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillRect(0, 0, cw, ch);

    const { x, y, size } = crop;
    // Clear crop area
    ctx.clearRect(x, y, size, size);
    ctx.drawImage(bmp, ox, oy, iw, ih);

    // Crop border — dark outline with light inner line for visibility on any background
    ctx.strokeStyle = 'rgba(0,0,0,0.7)';
    ctx.lineWidth = 3;
    ctx.strokeRect(x, y, size, size);
    ctx.strokeStyle = 'rgba(255,255,255,0.55)';
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 1, y + 1, size - 2, size - 2);

    // Corner handles
    const h = 9;
    const handles: [number, number][] = [
      [x, y],
      [x + size - h, y],
      [x, y + size - h],
      [x + size - h, y + size - h],
    ];
    handles.forEach(([hx, hy]) =>
    {
      ctx.fillStyle = 'rgba(0,0,0,0.75)';
      ctx.fillRect(hx, hy, h, h);
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.fillRect(hx + 1, hy + 1, h - 2, h - 2);
    });

    // Update preview
    drawPreview(bmp, ox, oy, scale);
  }, [imageBitmap, crop]);

  const drawPreview = useCallback(
    (bmp: ImageBitmap, ox: number, oy: number, scale: number) =>
    {
      const pv = previewRef.current;
      if (!pv) return;
      const { x, y, size } = crop;
      const ctx = pv.getContext('2d')!;
      ctx.clearRect(0, 0, pv.width, pv.height);

      // Map crop rect back to original image coords
      const srcX = (x - ox) / scale;
      const srcY = (y - oy) / scale;
      const srcSize = size / scale;

      ctx.drawImage(bmp, srcX, srcY, srcSize, srcSize, 0, 0, pv.width, pv.height);
    },
    [crop],
  );

  useEffect(() =>
  {
    if (imageBitmap) drawCanvas();
  }, [imageBitmap, crop, drawCanvas]);

  /* ── Clamp crop inside canvas ────────────────────── */
  const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

  /* ── Mouse events on canvas ──────────────────────── */
  const getPos = (e: React.MouseEvent<HTMLCanvasElement>) =>
  {
    const r = canvasRef.current!.getBoundingClientRect();
    return { mx: e.clientX - r.left, my: e.clientY - r.top };
  };

  /* ── Identify which corner the mouse is near ─────── */
  const getHandleCorner = (
    mx: number,
    my: number,
    c: CropRect,
  ): 'TL' | 'TR' | 'BL' | 'BR' | null =>
  {
    const { x, y, size } = c;
    const h = 14; // hit radius
    if (mx >= x - h && mx <= x + h && my >= y - h && my <= y + h) return 'TL';
    if (mx >= x + size - h && mx <= x + size + h && my >= y - h && my <= y + h) return 'TR';
    if (mx >= x - h && mx <= x + h && my >= y + size - h && my <= y + size + h) return 'BL';
    if (mx >= x + size - h && mx <= x + size + h && my >= y + size - h && my <= y + size + h)
      return 'BR';
    return null;
  };

  const isInsideCrop = (mx: number, my: number) =>
  {
    const { x, y, size } = crop;
    return mx >= x && mx <= x + size && my >= y && my <= y + size;
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) =>
  {
    const { mx, my } = getPos(e);
    const corner = getHandleCorner(mx, my, crop);
    if (corner)
    {
      resizing.current = corner;
    }
    else if (isInsideCrop(mx, my))
    {
      dragging.current = true;
      dragStart.current = { mx, my, cx: crop.x, cy: crop.y };
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) =>
  {
    const canvas = canvasRef.current!;
    const cw = canvas.width;
    const ch = canvas.height;
    const { mx, my } = getPos(e);

    if (dragging.current)
    {
      const dx = mx - dragStart.current.mx;
      const dy = my - dragStart.current.my;
      setCrop(prev => ({
        ...prev,
        x: clamp(dragStart.current.cx + dx, 0, cw - prev.size),
        y: clamp(dragStart.current.cy + dy, 0, ch - prev.size),
      }));
    }
    else if (resizing.current !== null)
    {
      const corner = resizing.current;
      setCrop(prev =>
      {
        // Anchor = opposite corner (stays fixed during resize)
        const ax = corner === 'TL' || corner === 'BL' ? prev.x + prev.size : prev.x;
        const ay = corner === 'TL' || corner === 'TR' ? prev.y + prev.size : prev.y;

        // New size = max distance from anchor to mouse, kept square
        let newSize = Math.max(Math.abs(mx - ax), Math.abs(my - ay), MIN_CROP);

        // Clamp so we don't go out of canvas bounds
        const maxByX = corner === 'TL' || corner === 'BL' ? ax : cw - ax;
        const maxByY = corner === 'TL' || corner === 'TR' ? ay : ch - ay;
        newSize = Math.min(newSize, maxByX, maxByY);

        // Compute new top-left from anchor
        const newX = corner === 'TL' || corner === 'BL' ? ax - newSize : ax;
        const newY = corner === 'TL' || corner === 'TR' ? ay - newSize : ay;

        return { x: newX, y: newY, size: newSize };
      });
    }
    else
    {
      // Cursor hint
      const corner = getHandleCorner(mx, my, crop);
      if (corner === 'TL' || corner === 'BR')
      {
        canvas.style.cursor = 'nwse-resize';
      }
      else if (corner === 'TR' || corner === 'BL')
      {
        canvas.style.cursor = 'nesw-resize';
      }
      else if (isInsideCrop(mx, my))
      {
        canvas.style.cursor = 'move';
      }
      else
      {
        canvas.style.cursor = 'crosshair';
      }
    }
  };

  const handleMouseUp = () =>
  {
    dragging.current = false;
    resizing.current = null;
  };

  /* ── Initialize crop when image loads ───────────── */
  useEffect(() =>
  {
    if (!imageBitmap || !canvasRef.current) return;
    const cw = canvasRef.current.width;
    const ch = canvasRef.current.height;
    const defaultSize = Math.min(cw, ch) * 0.6;
    setCrop({
      x: (cw - defaultSize) / 2,
      y: (ch - defaultSize) / 2,
      size: defaultSize,
    });
  }, [imageBitmap]);

  /* ── Save: export crop as Blob ───────────────────── */
  const handleSave = async () =>
  {
    if (!imageBitmap || !canvasRef.current || !selectedFile) return;
    setSaving(true);

    const canvas = canvasRef.current;
    const scale = Math.min(canvas.width / imageBitmap.width, canvas.height / imageBitmap.height);
    const ox = (canvas.width - imageBitmap.width * scale) / 2;
    const oy = (canvas.height - imageBitmap.height * scale) / 2;

    const { x, y, size } = crop;
    const srcX = (x - ox) / scale;
    const srcY = (y - oy) / scale;
    const srcSize = size / scale;

    const out = document.createElement('canvas');
    out.width = 200;
    out.height = 200;
    out.getContext('2d')!.drawImage(imageBitmap, srcX, srcY, srcSize, srcSize, 0, 0, 200, 200);

    out.toBlob(
      async blob =>
      {
        if (!blob)
        {
          setSaving(false);
          return;
        }
        try
        {
          await onSave(blob, selectedFile.name);
          onClose();
        }
        finally
        {
          setSaving(false);
        }
      },
      'image/jpeg',
      0.92,
    );
  };

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) =>
  {
    if (e.target === e.currentTarget) onClose();
  };

  /* ── Render ──────────────────────────────────────── */
  return (
    <div className="iup-backdrop" onClick={handleBackdrop}>
      <div className="iup-modal">
        {/* Header */}
        <div className="iup-header">
          <span className="iup-title">
            {step === 1 ? 'Add Profile Image' : 'Crop Profile Image'}
          </span>
          <button className="iup-close" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Body */}
        <div className="iup-body">
          {step === 1 ? (
            <>
              <p className="iup-step-label">
                <strong>Step 1:</strong> Select an image (JPG,PNG,JPEG). The image should not exceed
                1 MB.
              </p>

              {errorMsg && <p className="iup-error">{errorMsg}</p>}

              <div className="iup-select-row">
                <button className="iup-btn-select" onClick={() => fileInputRef.current?.click()}>
                  Select Image
                </button>
                {fileName && <span className="iup-filename">{fileName}</span>}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png"
                style={{ display: 'none' }}
                onChange={handleSelectFile}
              />
            </>
          ) : (
            <>
              <p className="iup-step-label">
                <strong>Step 2:</strong> Select the area of the image you want to display.
              </p>
              <div className="iup-crop-area">
                <canvas
                  ref={canvasRef}
                  width={380}
                  height={320}
                  className="iup-canvas"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                />
                <canvas ref={previewRef} width={150} height={150} className="iup-preview" />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="iup-footer">
          {step === 1 ? (
            <>
              <button className="iup-btn iup-btn-cancel" onClick={onClose}>
                Cancel
              </button>
              <button
                className="iup-btn iup-btn-next"
                disabled={!selectedFile}
                onClick={() => setStep(2)}
              >
                Next
              </button>
            </>
          ) : (
            <>
              <button className="iup-btn iup-btn-cancel" onClick={() => setStep(1)}>
                Cancel
              </button>
              <button className="iup-btn iup-btn-next" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageUploadModal;
